import React, { useContext, useMemo, useState, useEffect } from "react";
import { dashboardContext, Access } from "../Dashboard";
import {
  Table,
  Pagination,
  RowsPerPageSelect,
} from "../../../components/Table";
import TransactionDateFilter from "../Transaction/components/TransactionDateFilter";
import { HiMiniXMark } from "react-icons/hi2";
import { IoSearchOutline } from "react-icons/io5";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import Modal from "../../../components/Modal/Modal";
import CreateSubscriptionForm from "./CreateSubscriptionForm";
import { CustomDropdownIndicator } from "../Settlement/Settlement";
import {
  GET_SUBSCRIPTIONS_BY_SCHOOL_IDS,
  MANAGE_SUBSCRIPTION,
  RAISE_SUBSCRIPTION_PAYMENT,
} from "../../../Qurries";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import Select from "react-select";
import { toast } from "react-toastify";
import { Input } from "edviron-ui-package";

type RowItem = {
  createdOn: string;
  createdOnIso?: string | null;
  subscriptionId: string;
  subscriptionRefNo: string;
  maximumAmount: string;
  recurringAmount: string;
  name: string;
  mobile: string;
  paymentMode: string;
  status: string;
  school_id: string;
  plan_id: any;
  __raw?: any;
};

function formatIsoDate(iso?: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString();
}

function ManageSubscriptionModal({
  open,
  setOpen,
  subscriptionId,
  subscriptionSchoolId,
  parentRefetch,
  resolvedSubscriptionProp,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  subscriptionId: string | null;
  subscriptionSchoolId?: string | null;
  parentRefetch?: (() => Promise<any>) | undefined;
  resolvedSubscriptionProp?: any | null;
}) {
  const [, setAction] = useState<string>("");
  const [planId, setPlanId] = useState<string>("");
  const [, setSubmitting] = useState(false);
  const [showRaisePayment, setShowRaisePayment] = useState(false);
  const [manageSubscriptionMutation] = useMutation(MANAGE_SUBSCRIPTION);
  const [showActionMenu, setShowActionMenu] = useState(false);

  const inputProps = (nameProp: string, required: boolean) => ({
    name: (
      <span className="flex items-center gap-1">
        {nameProp}
        {required && <span className="text-red-500">*</span>}
      </span>
    ),
    add_error: () => {},
  });

  const handleActionClick = (value: string) => {
    setShowActionMenu(false);
    setPendingAction(value);
  };

  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const ACTION_CONFIRMATION: Record<
    string,
    { title: string; description: string; confirmText: string }
  > = {
    ACTIVATE: {
      title: "Activate Subscription",
      description: "This subscription will be activated.",
      confirmText: "Yes, Activate",
    },
    PAUSE: {
      title: "Pause Subscription",
      description:
        "This subscription will be temporarily paused and no payments will be processed.",
      confirmText: "Yes, Pause",
    },
    CANCEL: {
      title: "Cancel Subscription",
      description:
        "You will not be able to use this subscription after you cancel it.",
      confirmText: "Yes, Cancel",
    },
    CHANGE_PLAN: {
      title: "Change Subscription Plan",
      description:
        "The subscription will be switched to the new plan you selected.",
      confirmText: "Yes, Change Plan",
    },
  };

  // const selectStyles = {
  //   control: (provided: any) => ({
  //     ...provided,
  //     backgroundColor: "#F6F8FA",
  //     border: "1px solid #1B163B",
  //     borderRadius: 4,
  //     minHeight: 20,
  //     boxShadow: "none",
  //   }),
  //   valueContainer: (provided: any) => ({ ...provided, padding: "5px 8px" }),
  //   input: (provided: any) => ({ ...provided, margin: 0, padding: 0 }),
  //   indicatorsContainer: (provided: any) => ({ ...provided, paddingRight: 8 }),
  // };

  const ACTION_OPTIONS = [
    { label: "Activate", value: "ACTIVATE" },
    { label: "Pause", value: "PAUSE" },
    { label: "Cancel", value: "CANCEL" },
    { label: "Change Plan", value: "CHANGE_PLAN" },
  ];

  const resolvedSubscription = resolvedSubscriptionProp ?? null;
  const plan = resolvedSubscription?.plan_details ?? null;
  const auth = resolvedSubscription?.authorization_details ?? null;
  const subscriptionStatus = resolvedSubscription?.subscription_status ?? "";

  const handleSubmit = async (forcedAction: string) => {
    if (!forcedAction) {
      toast.error("Select an action");
      return;
    }
    if (forcedAction === "CHANGE_PLAN" && !planId) {
      toast.error("Please enter a Plan ID");
      return;
    }

    if (!subscriptionId) {
      toast.error("Missing subscription id");
      return;
    }

    try {
      await parentRefetch?.();
    } catch {}

    const resolvedSchoolId =
      subscriptionSchoolId ??
      resolvedSubscription?.plan_details?.school_id ??
      null;

    if (!resolvedSchoolId) {
      toast.error("school_id not available. Cannot proceed.");
      return;
    }

    const action_details =
      forcedAction === "CHANGE_PLAN"
        ? { plan_id: planId || undefined }
        : undefined;

    const variables = {
      manageSubscription: {
        subscription_id: resolvedSubscriptionProp?.subscription_id || "",
        school_id: String(resolvedSchoolId),
        action: forcedAction,
        action_details,
      },
    };

    try {
      setSubmitting(true);
      const { data } = await manageSubscriptionMutation({ variables });
      const msg =
        data?.manageMerchantSubscription?.message || "Subscription updated";
      toast.success(msg);
      setOpen(false);
      setAction("");
      setPlanId("");
    } catch (err: any) {
      const message =
        err?.graphQLErrors?.[0]?.message || err?.message || "Failed to update";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (open && !resolvedSubscription) {
    return (
      <Modal
        open={open}
        setOpen={setOpen}
        title=""
        className="max-w-xl w-full max-h-[90vh] overflow-hidden"
      >
        <div className="p-6 text-center">Loading subscription…</div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title=""
      className="max-w-5xl w-full max-h-[90vh] overflow-hidden"
    >
      <div className="flex items-center justify-between border-b px-6 py-4">
        <div>
          <div className="text-sm text-gray-500">Subscription ID</div>
          <div className="text-lg font-semibold">
            {resolvedSubscription?.subscription_id ?? subscriptionId ?? "-"}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="px-3 py-1 rounded-[4px] bg-blue-100 text-blue-800 text-sm">
            {resolvedSubscription?.subscription_status ?? "-"}
          </span>

          <button
            disabled={subscriptionStatus === "CANCELLED"}
            onClick={() => setShowRaisePayment(true)}
            className="bg-[#1E1B59] text-white text-xs font-medium px-4 py-2 rounded-[6px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Raise Payment Request
          </button>

          <div className="relative">
            <button
              onClick={() => setShowActionMenu((v) => !v)}
              className="bg-[#1E1B59] text-white text-xs font-medium px-4 py-2 rounded-[6px] flex items-center gap-1"
            >
              Manage Subscription
              <span className="text-sm">▾</span>
            </button>

            {showActionMenu && (
              <div className="absolute right-0 mt-2 w-44 bg-white border rounded-[6px] shadow-lg z-50">
                {ACTION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleActionClick(opt.value)}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {pendingAction && (
        <Modal
          open={!!pendingAction}
          setOpen={() => setPendingAction(null)}
          title={ACTION_CONFIRMATION[pendingAction].title}
          className="max-w-md w-full"
        >
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-6">
              {ACTION_CONFIRMATION[pendingAction].description}
            </p>

            <div className="mb-6">
              {pendingAction === "CHANGE_PLAN" && (
                <Input
                  {...inputProps("Plan ID", true)}
                  type="text"
                  placeholder="Enter new plan ID"
                  value={planId}
                  onChange={(a: any, b?: any) =>
                    setPlanId(
                      b === undefined
                        ? typeof a === "string"
                          ? a
                          : a?.target?.value ?? ""
                        : b
                    )
                  }
                />
              )}
            </div>

            <div className="flex justify-between gap-4">
              <button
                onClick={() => setPendingAction(null)}
                className="text-gray-600 text-sm font-medium"
              >
                No
              </button>

              <button
                disabled={pendingAction === "CHANGE_PLAN" && !planId}
                onClick={async () => {
                  const confirmedAction = pendingAction!;
                  setPendingAction(null);

                  await handleSubmit(confirmedAction);

                  setAction("");
                  setPlanId("");
                }}
                className="bg-red-600 text-white px-4 py-2 rounded-[6px] text-sm font-medium
             disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ACTION_CONFIRMATION[pendingAction].confirmText}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
        <div className="bg-white border rounded-[4px]">
          <div className="px-6 py-4 border-b">
            <div className="text-lg font-semibold">Customer Details</div>
          </div>
          <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 text-sm">
            <div className="flex justify-between">
              <div className="text-gray-600">Customer Name</div>
              <div className="font-medium">
                {resolvedSubscription?.customer_details?.customer_name ?? "-"}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-gray-600">Mobile No.</div>
              <div className="font-medium">
                {resolvedSubscription?.customer_details?.customer_phone ?? "-"}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-gray-600">Email</div>
              <div className="font-medium">
                {resolvedSubscription?.customer_details?.customer_email ?? "-"}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-gray-600">Payment Mode</div>
              <div className="font-medium">{auth?.payment_methods ?? "--"}</div>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="bg-white border rounded-[4px]">
          <div className="px-6 py-4 border-b">
            <div className="text-lg font-semibold">Subscription Details</div>
          </div>
          <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 text-sm">
            <div className="flex justify-between">
              <div className="text-gray-600">Reference ID</div>
              <div className="font-medium">
                {resolvedSubscription?.cf_subscription_id ?? "-"}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Plan Name</div>
              <div className="font-medium">
                {resolvedSubscription?.plan_details?.plan_name ?? "-"}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Created On</div>
              <div className="font-medium">
                {resolvedSubscription?.createdAt
                  ? formatIsoDate(resolvedSubscription.createdAt)
                  : resolvedSubscription?.subscription_first_charge_time
                  ? formatIsoDate(
                      resolvedSubscription.subscription_first_charge_time
                    )
                  : "-"}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Activated On</div>
              <div className="font-medium">--</div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Subscription Type</div>
              <div className="font-medium">
                {plan?.plan_type === "ON_DEMAND"
                  ? "On Demand"
                  : plan?.plan_type ?? "-"}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Authorized At</div>
              <div className="font-medium">
                {auth?.authorization_time
                  ? formatIsoDate(auth.authorization_time)
                  : "--"}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Authorization Link</div>
              <div className="font-medium break-words">
                <a
                  href={resolvedSubscription?.edv_authorization_link ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  tabIndex={-1}
                  className="text-purple-600 underline"
                >
                  {resolvedSubscription?.edv_authorization_link
                    ? "Open Link"
                    : "--"}
                </a>
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Expiry Date</div>
              <div className="font-medium">
                {resolvedSubscription?.subscription_expiry_time
                  ? formatIsoDate(resolvedSubscription.subscription_expiry_time)
                  : "-"}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Max No. of Debits</div>
              <div className="font-medium">
                {plan?.plan_max_cycles ??
                  (plan?.plan_max_cycles === 0 ? "0" : "--")}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Last Updated On</div>
              <div className="font-medium">
                {resolvedSubscription?.updatedAt
                  ? formatIsoDate(resolvedSubscription.updatedAt)
                  : "-"}
              </div>
            </div>

            <div className="flex justify-between">
              <div className="text-gray-600">Maximum Amount</div>
              <div className="font-medium">
                {plan?.plan_max_amount != null
                  ? `INR ${Number(plan.plan_max_amount).toFixed(2)}`
                  : "-"}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Requests & Transactions */}
        <div className="bg-white border rounded-[4px]">
          <div className="px-6 py-4 border-b">
            <div className="text-lg font-semibold">
              Payment Requests & Transactions
            </div>
          </div>
          <div className="px-6 py-6 text-sm">
            <div className="text-gray-600">No transactions to display</div>
          </div>
        </div>
      </div>

      <RaisePaymentModal
        open={showRaisePayment}
        setOpen={setShowRaisePayment}
        subscription={resolvedSubscription}
      />
    </Modal>
  );
}

function RaisePaymentModal({
  open,
  setOpen,
  subscription,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  subscription: any;
}) {
  const [amount, setAmount] = useState("");
  const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const [scheduleDate, setScheduleDate] = useState<Date>(() => getTomorrow());

  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const parsedAmount = Number(amount);

  const [raisePayment] = useMutation(RAISE_SUBSCRIPTION_PAYMENT);

  const maxAmount = subscription?.plan_details?.plan_max_amount;
  const maxAllowed = Number(maxAmount);

  const handleSubmit = async () => {
    if (!amount) {
      toast.error("Payment amount is required");
      return;
    }

    if (Number.isNaN(parsedAmount)) {
      toast.error("Invalid payment amount");
      return;
    }

    if (parsedAmount <= 0) {
      toast.error("Payment amount must be greater than 0");
      return;
    }

    if (maxAllowed && parsedAmount > maxAllowed) {
      toast.error(`Payment amount cannot exceed ₹ ${maxAllowed}`);
      return;
    }

    const finalDate = scheduleDate!;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const scheduled = new Date(finalDate);
    scheduled.setHours(0, 0, 0, 0);

    if (scheduled < today) {
      toast.error("Payment cannot be scheduled in the past");
      return;
    }

    try {
      setLoading(true);

      await raisePayment({
        variables: {
          input: {
            subscription_id: subscription.subscription_id,
            school_id: String(subscription.plan_details.school_id),
            payment_type: "CHARGE",
            payment_amount: parsedAmount,
            payment_schedule_date: finalDate.toISOString(),
            payment_remarks: remarks || undefined,
          },
        },
      });

      toast.success("Payment request raised");
      setOpen(false);
      setAmount("");
      setRemarks("");
      setScheduleDate(getTomorrow());
    } catch (err: any) {
      toast.error(
        err?.graphQLErrors?.[0]?.message || "Failed to raise payment"
      );
    } finally {
      setLoading(false);
    }
  };

  const getValue = (a: any, b?: any) =>
    b === undefined ? (typeof a === "string" ? a : a?.target?.value ?? "") : b;

  const tomorrowISO = getTomorrow().toISOString().split("T")[0];

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Raise Payment Request"
      className="max-w-md w-full"
    >
      <div className="p-6 space-y-4 text-sm">
        <Input
          name="Subscription ID"
          value={subscription.subscription_id}
          disabled
          add_error={() => {}}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            name="Payment Amount"
            required
            value={amount}
            add_error={() => {}}
            type="number"
            min={0}
            max={maxAllowed}
            placeholder="Enter amount"
            onChange={(a: any, b?: any) => {
              const v = getValue(a, b);
              if (v === "") {
                setAmount("");
                return;
              }
              if (!/^\d*\.?\d*$/.test(v)) return;

              setAmount(v);
            }}
          />

          <Input
            name="Maximum Amount"
            value={`₹ ${maxAmount}`}
            disabled
            add_error={() => {}}
          />
        </div>

        <Input
          name="Schedule On"
          type="date"
          min={tomorrowISO}
          value={scheduleDate?.toISOString().split("T")[0]}
          add_error={() => {}}
          onChange={(a: any, b?: any) => {
            const v = getValue(a, b);
            setScheduleDate(new Date(v));
          }}
        />

        <Input
          name="Remarks"
          textarea
          maxLength={200}
          value={remarks}
          add_error={() => {}}
          onChange={(a: any, b?: any) => setRemarks(getValue(a, b))}
          placeholder="Optional (max 200 characters)"
        />

        <div className="flex justify-end gap-3 pt-4">
          <button
            onClick={() => setOpen(false)}
            className="px-4 py-2 border rounded-[4px]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-[#5b21b6] text-white rounded-[4px]"
          >
            {loading ? "Processing..." : "Raise Payment"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function Subscriptions() {
  const { user, isRedirectedUser, tokenData } = useContext(dashboardContext);
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerRow, setItemsPerRow] = useState<{ name: number }>({
    name: 10,
  });
  const [selectedRange, setSelectedRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  const [isDateRangeIsSelected, setIsDateRangeIsSelected] = useState(false);
  const [dateRangeLabel, setDateRangeLabel] = useState<string>("");

  const [showCreateSubscription, setShowCreateSubscription] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    string | null
  >(null);
  const [searchFilter, setSearchFilter] = useState<string>("");

  const [selectedSubscriptionSchoolId, setSelectedSubscriptionSchoolId] =
    useState<string | null>(null);
  const [resolvedSubscriptionForModal, setResolvedSubscriptionForModal] =
    useState<any | null>(null);

  const client = useApolloClient();

  const searchByOptions = [
    {
      label: "Subs. ID",
      value: "subscription_id",
    },
    {
      label: "Subs. Ref. No.",
      value: "cf_subscription_id",
    },
    {
      label: "Plan ID",
      value: "plan_id",
    },
    {
      label: "Mobile No.",
      value: "customer_details.customer_phone",
    },
  ];

  const searchBy = searchFilter || undefined;

  const { data, loading, refetch } = useQuery(GET_SUBSCRIPTIONS_BY_SCHOOL_IDS, {
    fetchPolicy: "network-only",
    variables: {
      filters: {
        page: currentPage,
        limit: itemsPerRow.name,
        search: searchText || undefined,
        searchBy,
        startDate: isDateRangeIsSelected
          ? selectedRange.startDate.toISOString()
          : undefined,
        endDate: isDateRangeIsSelected
          ? selectedRange.endDate.toISOString()
          : undefined,
      },
    },
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      refetch({
        filters: {
          page: 1,
          limit: itemsPerRow.name,
          search: searchText?.trim() || undefined,
          searchBy,
          startDate: isDateRangeIsSelected
            ? selectedRange.startDate.toISOString()
            : undefined,
          endDate: isDateRangeIsSelected
            ? selectedRange.endDate.toISOString()
            : undefined,
        },
      });

      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timeout);
  }, [
    searchText,
    itemsPerRow.name,
    isDateRangeIsSelected,
    selectedRange,
    searchBy,
    refetch,
  ]);

  const rawList = useMemo(() => {
    if (!data?.merchantSubscriptionsBySchoolIds?.data) return [];
    return data.merchantSubscriptionsBySchoolIds.data;
  }, [data]);

  const allSubscriptions: RowItem[] = useMemo(() => {
    if (!Array.isArray(rawList)) return [];

    return rawList.map((s: any) => {
      const createdOnIso =
        s.createdAt ?? s.subscription_first_charge_time ?? null;
      const createdOn = createdOnIso ? formatIsoDate(createdOnIso) : "-";

      const plan = s.plan_details ?? {};
      const maximumAmount =
        plan.plan_max_amount != null
          ? `INR ${Number(plan.plan_max_amount).toFixed(2)}`
          : "-";
      const recurringAmount =
        plan.plan_recurring_amount != null
          ? `INR ${Number(plan.plan_recurring_amount).toFixed(2)}`
          : plan.plan_amount != null
          ? `INR ${Number(plan.plan_amount).toFixed(2)}`
          : "-";
      const name = plan.plan_name || s.customer_details?.customer_name || "-";
      const mobile = s.customer_details?.customer_phone || "-";

      return {
        createdOn,
        createdOnIso,
        subscriptionId: s.subscription_id ?? s.cf_subscription_id ?? "-",
        subscriptionRefNo: s.cf_subscription_id ?? "-",
        maximumAmount,
        recurringAmount,
        name,
        mobile,
        paymentMode: s.authorization_details?.payment_methods || "--",
        status: s.subscription_status || "-",
        school_id: s.plan_details?.school_id || "-",
        plan_id: s.plan_details?.plan_id || "-",
        __raw: s,
      } as RowItem;
    });
  }, [rawList]);

  const totalPages = Math.ceil(
    (data?.merchantSubscriptionsBySchoolIds?.total || 0) / itemsPerRow.name
  );

  const pageData = allSubscriptions;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value: { name: number }) => {
    setItemsPerRow(value);
    setCurrentPage(1);
  };

  const openManageFor = async (row: RowItem) => {
    const raw = row.__raw || {};

    const subscription_id: string = raw.subscription_id;

    let resolvedSchoolId: any = raw.plan_details?.school_id ?? null;

    setSelectedSubscriptionId(subscription_id);
    setSelectedSubscriptionSchoolId(resolvedSchoolId);

    try {
      const resp = await client.query({
        query: GET_SUBSCRIPTIONS_BY_SCHOOL_IDS,
        variables: {
          filters: {
            page: 1,
            limit: 1,
          },
          subscription_id: subscription_id,
        },
        fetchPolicy: "network-only",
      });

      const respList = resp?.data?.merchantSubscriptionsBySchoolIds ?? null;

      if (respList) {
        const item = respList.data[0];
        setResolvedSubscriptionForModal(item);
        await Promise.resolve();
        setShowManageModal(true);
        return;
      }
    } catch (err: any) {
      console.error("[openManageFor] query error for", subscription_id, err);
    }
  };

  return (
    <div className="overflow-hidden">
      <Modal
        open={showCreateSubscription}
        setOpen={(v: boolean) => {
          setShowCreateSubscription(v);
          if (!v) refetch?.();
        }}
        title="Create Subscription"
        className="w-full max-w-xl"
      >
        <CreateSubscriptionForm
          setOpen={setShowCreateSubscription}
          onCreated={() => refetch?.()}
        />
      </Modal>

      <ManageSubscriptionModal
        key={selectedSubscriptionId ?? "manage-modal"}
        open={showManageModal}
        setOpen={(v) => {
          setShowManageModal(v);
          if (!v) {
            setSelectedSubscriptionId(null);
            setSelectedSubscriptionSchoolId(null);
            setResolvedSubscriptionForModal(null);
            refetch?.();
          }
        }}
        subscriptionId={selectedSubscriptionId}
        subscriptionSchoolId={selectedSubscriptionSchoolId}
        parentRefetch={refetch}
        resolvedSubscriptionProp={resolvedSubscriptionForModal}
      />

      <div className="overflow-x-auto w-full">
        <Table
          perPage={false}
          exportBtn={true}
          heading={"All Subscriptions"}
          pagination={false}
          loading={loading}
          copyContent={[3, 4, 7]}
          searchBox={
            <div className="w-full ">
              <div className="flex xl:!flex-row flex-col justify-between gap-2 w-full xl:items-center items-start mb-2">
                <div className="bg-[#EEF1F6] py-3 items-center flex px-3 xl:max-w-md max-w-[34rem] w-full rounded-[4px]">
                  <input
                    className="text-xs pr-2 bg-transparent focus:outline-none w-full placeholder:font-normal"
                    type="text"
                    value={searchText}
                    placeholder="Search (Subs. ID, Ref. No., Plan Name, Mobile...)"
                    onChange={(e) => {
                      handleSearch(e.target.value);
                    }}
                  />
                  {searchFilter !== "" && searchText.length > 1 && (
                    <HiMiniXMark
                      onClick={async () => {
                        setSearchFilter("");
                        setSearchText("");
                      }}
                      className="text-[#1E1B59] cursor-pointer text-md mr-2 shrink-0"
                    />
                  )}
                  <Select
                    className="border-l-2 border-gray-400"
                    options={searchByOptions}
                    isSearchable={false}
                    components={{
                      DropdownIndicator: CustomDropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    onChange={(e: any) => {
                      setSearchFilter(e?.value || "");
                      setCurrentPage(1);
                    }}
                    placeholder={
                      <div className="text-[#1E1B59] -mt-1 capitalize text-[10px]">
                        {searchFilter === ""
                          ? "Filter by"
                          : searchByOptions.find(
                              (o) => o.value === searchFilter
                            )?.label || "Filter by"}
                      </div>
                    }
                    value={
                      searchByOptions.find(
                        (opt) => opt.value === searchFilter
                      ) || null
                    }
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: "transparent",
                        height: "20px",
                        border: "none",
                        boxShadow: "none",
                        cursor: "pointer",
                        minHeight: "10px",
                        padding: "0px",
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        height: "20px",
                        width: "8rem",
                        padding: "0 8px",
                      }),
                      input: (provided) => ({
                        ...provided,
                        margin: "0",
                        padding: "0",
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        margin: "0",
                        padding: "0",
                        lineHeight: "20px",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: "#1E1B59",
                        fontSize: "10px",
                        textTransform: "capitalize",
                        marginTop: "-1px",
                        lineHeight: "20px",
                      }),

                      indicatorsContainer: (provided) => ({
                        ...provided,
                        height: "20px",
                      }),
                      option: (provided) => ({
                        ...provided,
                        fontSize: "10px",
                        cursor: "pointer",
                      }),
                    }}
                  />
                  <div className="w-10 z-50 shrink-0 flex justify-center items-center">
                    {loading ? (
                      <AiOutlineLoading3Quarters className="text-xs animate-spin" />
                    ) : (
                      <IoSearchOutline className="cursor-pointer text-edvion_black text-opacity-50 text-md" />
                    )}
                  </div>
                </div>

                <div className="flex justify-end items-center flex-1 w-full max-w-[24rem] gap-2">
                  <div className="w-full max-w-[15rem]">
                    <TransactionDateFilter
                      setType={setDateRangeLabel}
                      type={dateRangeLabel}
                      selectedRange={selectedRange}
                      setSelectedRange={setSelectedRange}
                      setIsDateRangeIsSelected={setIsDateRangeIsSelected}
                      refetch={() => {
                        // when date range is applied in the filter
                        setIsDateRangeIsSelected(true);
                        setCurrentPage(1);

                        refetch({
                          filters: {
                            page: 1,
                            limit: itemsPerRow.name,
                            search: searchText?.trim() || undefined,
                            searchBy,
                            startDate: selectedRange.startDate.toISOString(),
                            endDate: selectedRange.endDate.toISOString(),
                          },
                        });
                      }}
                    />
                  </div>
                  {((isRedirectedUser ? tokenData?.role : user?.getMerchantQuery?.role) === Access.OWNER || (isRedirectedUser ? tokenData?.role : user?.getMerchantQuery?.role) === Access.ADMIN) && (
                    <button
                      onClick={() => setShowCreateSubscription(true)}
                      className="bg-[#1E1B59] text-white text-xs font-medium px-4 py-2 rounded-[4px] whitespace-nowrap"
                    >
                      Create Subscription
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center">
                <RowsPerPageSelect
                  setItemsPerRow={handleItemsPerPageChange}
                  itemsPerRow={itemsPerRow}
                  className="justify-start"
                />
                {dateRangeLabel !== "" && isDateRangeIsSelected && (
                  <div className="text-sm m-2">
                    <button
                      onClick={() => {
                        setDateRangeLabel("");
                        setIsDateRangeIsSelected(false);

                        const resetRange = {
                          startDate: new Date(),
                          endDate: new Date(),
                          key: "selection",
                        };

                        setSelectedRange(resetRange);

                        refetch({
                          filters: {
                            page: 1,
                            limit: itemsPerRow.name,
                            search: searchText || undefined,
                            searchBy,
                          },
                        });

                        setCurrentPage(1);
                      }}
                      className="bg-[#6687FFCC] font-medium flex items-center rounded-[4px] text-white px-4 py-2 h-full w-full"
                    >
                      {dateRangeLabel}
                      <HiMiniXMark className="text-lg ml-1" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          }
          data={[
            [
              "Sr.No",
              "Created On",
              "Subs. ID",
              "Subs. Ref. No.",
              "School ID",
              "Maximum Amt.",
              "Recurring Amt.",
              "Plan ID",
              "Plan Name",
              "Mobile No.",
              "Payment Mode",
              "Status",
            ],
            ...pageData.map((row, index) => [
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {(currentPage - 1) * itemsPerRow.name + 1 + index}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {row.createdOn}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1 font-medium text-sm"
              >
                {row.subscriptionId}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {row.subscriptionRefNo}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {row.school_id}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {row.maximumAmount}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {row.recurringAmount}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {row.plan_id}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {row.name}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {row.mobile}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                {row.paymentMode}
              </div>,
              <div
                onClick={() => openManageFor(row)}
                className="cursor-pointer px-2 py-1"
              >
                <span className="text-[#6687FF]">{row.status}</span>
              </div>,
            ]),
          ]}
          footer={
            <div className="flex justify-center items-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          }
        />
      </div>
    </div>
  );
}
